import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, CartesianGrid, Cell,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { RESULT_TYPE_COLORS } from '../../../utils/dataProcessor';

/**
 * RegionalByTypeChart
 *
 * Horizontal bar chart showing the breakdown of results by result type
 * (Knowledge product, Innovation development, Policy change, etc.)
 * for a specific Region.  Each bar is coloured per its result type.
 */
const RegionalByTypeChart = memo(function RegionalByTypeChart({ results, color }) {
  const { chartData, exportData } = useMemo(() => {
    if (!results || results.length === 0) return { chartData: [], exportData: [] };

    const counts = {};
    for (const r of results) {
      const type = r.type || 'Unknown';
      counts[type] = (counts[type] || 0) + 1;
    }

    const sorted = Object.entries(counts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);

    const _exportData = sorted.map(d => ({
      'Result Type': d.type,
      'Result Count': d.count,
    }));

    return { chartData: sorted, exportData: _exportData };
  }, [results]);

  if (!chartData || chartData.length === 0) {
    return (
      <ChartWrapper title="Results by Type">
        <NoChartData
          message="No data available"
          detail="No result type data found for this Region."
        />
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper
      title="Results by Type"
      info="Breakdown of results by result type for this Region. Shows how results are distributed across knowledge products, innovations, policy changes, and other categories."
      chartData={exportData}
      sourceResults={results}
      exportFilename="regional-by-type"
    >
      <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 40)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 40, left: 8, bottom: 4 }}
          barCategoryGap="24%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="type"
            tick={{ fontSize: 11 }}
            width={180}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{
                  background: '#fff',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: '13px',
                  lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.type}</div>
                  <div style={{ color: '#4b5563' }}>
                    {d.count.toLocaleString()} result{d.count !== 1 ? 's' : ''}
                  </div>
                </div>
              );
            }}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.type} fill={RESULT_TYPE_COLORS[entry.type] || color} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});

RegionalByTypeChart.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default RegionalByTypeChart;
